import Vue from 'vue';
import { apolloProvider } from '@/graphql/vueApollo'; // GraphQL connection
import EventBus from '@/utilities/eventBus';
import logger from 'client-logger';
import formatGraphQlError from '@/utilities/formatGraphQlError';
import { FORBIDDEN } from '@/utilities/errorCodes';

// Our queries and mutations
import GET_USER_BY_ID from '@/graphql/queries/getUserById.gql';
import GET_USER_USERS from '@/graphql/queries/getUserUsers.gql';
import GET_ALL_USERS from '@/graphql/queries/getAllUsers.gql';
import CREATE_USER from '@/graphql/mutations/createUser.gql';
import UPDATE_USER from '@/graphql/mutations/updateUser.gql';
import DELETE_USER_BY_ID from '@/graphql/mutations/deleteUserById.gql';
import CREATE_USER_STORE_ROLE from '@/graphql/mutations/createUserStoreRole.gql';
import UPDATE_USER_STORE_ROLE from '@/graphql/mutations/updateUserStoreRole.gql';
import DELETE_USER_STORE_ROLE from '@/graphql/mutations/deleteUserStoreRoleById.gql';
import REGISTER from '@/graphql/mutations/register.gql';
import SUBSCRIBE_TO_SELF from '@/graphql/mutations/subscribeToSelf.gql';

// Error Handling
import AppState from '@/store/AppState';

const apollo = apolloProvider.defaultClient;

// An interface for getting, querying, and mutating our data.
const User = new Vue({
	data() {
		return {
			// The current user's ID
			id: null,

			// To track when we've made an update to this user and need to refresh
			selfUpdate: false,

			// A flag to tell us whether we've retrieved all users or not
			fetchedUsers: false,
			allUsers: {}
		};
	},
	computed: {
		// Fetches the current user
		currentUser() {
			return this.allUsers[this.id] || {};
		},

		// Returns the user specified by the ID.
		user() {
			return id => {
				if (!this.allUsers[id]) {
					// Start the fetch for the data!
					apollo
						.query({
							query: GET_USER_BY_ID,
							variables: {
								id
							},
							fetchPolicy: 'network-only'
						})
						.then(response => {
							// Update our user data object
							this.setUser(id, response.data.getUserById);
						})
						.catch(e => {
							// Returns a nicely formatted GraphQL error object. Cheers!
							const formattedError = formatGraphQlError(e);

							if (formattedError) {
								logger[formattedError.code === FORBIDDEN ? 'warn' : 'error'](
									{ error: formattedError, userId: id },
									'Failed to get user by ID'
								);
							} else {
								logger.error({ error: e, userId: id }, 'Failed to get user by ID');
							}

							// Set our user as undefined so that we know there was an error
							this.setUser(id, null);
						});
				}

				return this.allUsers[id];
			};
		},

		// Returns an object list of users that this user has access to view
		users() {
			if (!this.fetchedUsers) {
				// Start the fetch for the data!
				if (this.currentUser.role === 'admin') {
					apollo
						.query({
							query: GET_ALL_USERS,
							fetchPolicy: 'network-only'
						})
						.then(response => {
							// Update our user data
							const results = response.data.getAllUsers;
							for (const result of results) {
								this.setUser(result.id, result);
							}
						})
						.catch(e => {
							// Returns a nicely formatted GraphQL error object. Cheers!
							const formattedError = AppState.showGeneralError(e);
							logger.error({ error: (formattedError && formattedError.error) || e }, 'Failed to get all users');
						});
				} else {
					apollo
						.query({
							query: GET_USER_USERS,
							fetchPolicy: 'network-only'
						})
						.then(response => {
							// Update our user data - both successes and errors
							const results = response.data.getUserUsers;

							// Successfully retrieved
							for (const result of results.data) {
								this.setUser(result.id, result);
							}

							// Errors in retrieving
							for (const userId of results.errors) {
								this.setUser(userId, null);
							}
						})
						.catch(e => {
							// Returns a nicely formatted GraphQL error object. Cheers!
							const formattedError = AppState.showGeneralError(e);
							logger.error({ error: (formattedError && formattedError.error) || e }, 'Failed to get user users');
						});
				}

				// Mark this flag so we don't accidentally do this fetch again
				this.$set(this, 'fetchedUsers', true);
			}

			// Simple - grab and return all users
			return this.allUsers;
		}
	},
	methods: {
		// Convenience method for auth to call to write our current user to our users list instead of doing a separate fetch
		setCurrentUser(user) {
			// We rather than actually creating a whole new "getCurrentUser" query, just re-use get by ID and track the user and their ID
			this.$set(this, 'id', user.id);
			this.setUser(user.id, user);

			// The easiest way, perhaps, to get updates for this user is to simply ask for them and nothing else
			apollo
				.mutate({
					mutation: SUBSCRIBE_TO_SELF
				})
				.catch(err => {
					const formattedError = formatGraphQlError(err);
					logger.error({ error: formattedError }, 'Failed to subscribe user to self');
				});
		},

		// Set the user's details in our users object
		setUser(id, user) {
			const existing = this.allUsers[id] || {};
			this.$set(this.allUsers, id, user ? { ...existing, ...user } : user); // Either merge if we have a user or set it as null
		},

		// Remove the user's details from our list
		removeUser(id) {
			this.$delete(this.allUsers, id);
		},

		// Creates a brand new user with the given props
		async createUser(userJson) {
			try {
				const {
					data: { createUser: newUser }
				} = await apollo.mutate({
					mutation: CREATE_USER,
					variables: {
						userJson
					}
				});

				// Update the user locally
				this.setUser(newUser.id, newUser);

				return {
					data: newUser
				};
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ error: (formattedError && formattedError.error) || e, userJson }, 'Error trying to create user');

				return formattedError;
			}
		},

		// Updates a user with the given ID and props
		async updateUser(id, props) {
			try {
				const {
					data: { updateUser: user }
				} = await apollo.mutate({
					mutation: UPDATE_USER,
					variables: {
						id,
						props
					}
				});

				// Update our users list
				this.setUser(id, user);

				return {
					data: user
				};
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ error: (formattedError && formattedError.error) || e, userId: id }, 'Error trying to update user');

				return formattedError;
			}
		},

		// Deletes the user with the given ID
		async deleteUserById(id) {
			try {
				const {
					data: { deleteUserById: result }
				} = await apollo.mutate({
					mutation: DELETE_USER_BY_ID,
					variables: {
						id
					}
				});

				// If we successfully deleted a user, than remove it from the users list
				if (Object.keys(result).length) {
					this.removeUser(id);
				}

				return {
					data: result
				};
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ error: (formattedError && formattedError.error) || e, userId: id }, 'Error trying to delete user');

				return formattedError;
			}
		},

		// Set a user's store role in their profile
		setUserStoreRole(userId, userStoreRole) {
			// Grab the user, of course
			const user = this.user(userId);

			if (user) {
				const existingRole = user.storeRoles.find(role => role.storeId === userStoreRole.storeId);

				// Update if exists; add if not
				if (existingRole) {
					Object.assign(existingRole, userStoreRole);
				} else {
					user.storeRoles.push(userStoreRole);
					this.$set(user, 'storeRoles', user.storeRoles);
				}
			}
		},

		// Remove a user's store role from their profile
		removeUserStoreRole(userId, storeId) {
			// Grab the user, of course
			const user = this.user(userId);

			if (user) {
				const roleToDelete = user.storeRoles.find(role => role.storeId === storeId);

				// Delete it if found
				if (roleToDelete) {
					const idx = user.storeRoles.indexOf(roleToDelete);
					user.storeRoles.splice(idx, 1);
					this.$set(user, 'storeRoles', user.storeRoles);
				}
			}
		},

		// Create a new UserStoreRole adding it to the given user
		async createUserStoreRole(userId, storeId, role) {
			try {
				const {
					data: { createUserStoreRole: newRole }
				} = await apollo.mutate({
					mutation: CREATE_USER_STORE_ROLE,
					variables: {
						userId,
						storeId,
						role
					}
				});

				// Update our user's list of store roles
				this.setUserStoreRole(userId, newRole);

				return {
					data: newRole
				};
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ error: (formattedError && formattedError.error) || e, userId, storeId, role }, 'Error trying to create userStoreRole');

				return formattedError;
			}
		},

		// Updates a UserStoreRole with the given userId, storeId, roleId
		async updateUserStoreRole(userId, storeId, roleId) {
			try {
				const {
					data: { updateUserStoreRole: userStoreRole }
				} = await apollo.mutate({
					mutation: UPDATE_USER_STORE_ROLE,
					variables: {
						userId,
						storeId,
						roleId
					}
				});

				// Update our user's list of store roles
				this.setUserStoreRole(userId, userStoreRole);

				return {
					data: userStoreRole
				};
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error(
					{ error: (formattedError && formattedError.error) || e, userId, storeId, role: roleId },
					'Error trying to update userStoreRole'
				);

				return formattedError;
			}
		},

		// Deletes the UserStoreRole with the given IDs
		async deleteUserStoreRole(userId, storeId) {
			try {
				const {
					data: { deleteUserStoreRoleById: result }
				} = await apollo.mutate({
					mutation: DELETE_USER_STORE_ROLE,
					variables: {
						userId,
						storeId
					}
				});

				// Remove this from our user's list of store roles
				this.removeUserStoreRole(userId, storeId);

				return {
					data: result
				};
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ error: (formattedError && formattedError.error) || e, userId, storeId }, 'Error trying to delete userStoreRole');

				return formattedError;
			}
		},

		// Registers the user with their given information
		async register(firstName, lastName, email, phone) {
			try {
				const { data } = await apollo.mutate({
					mutation: REGISTER,
					variables: {
						firstName,
						lastName,
						email,
						phone
					}
				});

				if (data.register.error) {
					return {
						error: data.register.error
					};
				}

				return {
					data: data.register.result
				};
			} catch (error) {
				const formattedError = AppState.showGeneralError(error);
				logger.error(
					{ error: (formattedError && formattedError.error) || error, firstName, lastName, email, phone },
					'Error trying to register user'
				);

				return formattedError;
			}
		},

		// A method that will accept incoming subscription payloads and do with them what needs to be done
		subscriptionHandler(payload) {
			let userId;
			if (payload.userStoreRole) {
				userId = payload.userStoreRole.userId;
			} else {
				userId = payload.user.id;
			}

			// If the current user has been updated, but from another source, then we should just reload the page so they get a fresh user token
			if (userId === this.id && !this.selfUpdate) {
				window.location.reload(false);
			}

			// We could have very different actions to execute depending on the scenario
			switch (payload.action) {
				case 'create':
				case 'update':
					this.setUser(userId, payload.user);
					break;
				case 'delete':
					this.removeUser(userId);
					break;
				case 'createRole':
				case 'updateRole':
					this.setUserStoreRole(userId, payload.userStoreRole);
					break;
				case 'deleteRole':
					this.removeUserStoreRole(userId, payload.userStoreRole.storeId);
					break;
			}
		},

		// Wipes everything out that we have available directly through our props
		clearAll() {
			this.$set(this, 'id', null);
			this.$set(this, 'selfUpdate', false);
			this.$set(this, 'fetchedUsers', false);
			this.$set(this, 'allUsers', {});
		}
	},
	created() {
		// Now to hook into real-time data updates
		EventBus.$on('user', this.subscriptionHandler);
	}
});

export default User;
